import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, FileText, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import StatCard from '../Common/StatCard';
import DataTable from '../Common/DataTable';
import SearchBar from '../Common/SearchBar';
import StatusBadge from '../Common/StatusBadge';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';
import './Dashboard.css';

export default function UniversityDashboard({ user }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [casesData, statsData] = await Promise.all([
        api.cases.list({ limit: 10, search, status: statusFilter, university_id: user?.university_id }),
        api.cases.getStats(),
      ]);
      setCases(Array.isArray(casesData) ? casesData : (casesData.cases || casesData.data || []));
      setStats(statsData.stats || statsData || {});
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, user?.university_id]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const columns = [
    { key: 'case_number', label: 'Case ID', sortable: true },
    { key: 'applicant_name', label: 'Applicant', sortable: true },
    { key: 'course_name', label: 'Course', sortable: true },
    { key: 'status', label: 'Status', render: (v) => <StatusBadge status={v} /> },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1>University Dashboard</h1>
          <p className="text-gray">Your verification cases overview</p>
        </div>
        <div className="dashboard-header-actions">
          <button className="btn-refresh" onClick={fetchDashboardData} title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card stat-card-blue" onClick={() => setStatusFilter('')}>
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap blue">
              <FileText size={22} />
            </div>
          </div>
          <div className="stat-card-value">{stats.total || 0}</div>
          <div className="stat-card-title">Total Cases</div>
        </div>

        <div className="stat-card stat-card-orange" onClick={() => setStatusFilter('in_process')}>
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap orange">
              <Clock size={22} />
            </div>
          </div>
          <div className="stat-card-value">{stats.in_process || 0}</div>
          <div className="stat-card-title">In Progress</div>
        </div>

        <div className="stat-card stat-card-yellow" onClick={() => setStatusFilter('pending_clarification')}>
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap yellow">
              <AlertCircle size={22} />
            </div>
          </div>
          <div className="stat-card-value">{stats.pending || 0}</div>
          <div className="stat-card-title">Pending</div>
        </div>

        <div className="stat-card stat-card-green" onClick={() => setStatusFilter('completed')}>
          <div className="stat-card-header">
            <div className="stat-card-icon-wrap green">
              <CheckCircle size={22} />
            </div>
          </div>
          <div className="stat-card-value">{stats.completed || 0}</div>
          <div className="stat-card-title">Completed</div>
        </div>
      </div>

      <div className="dashboard-table-section">
        <div className="table-header">
          <h2>Your Cases</h2>
          <div className="dashboard-filters">
            <SearchBar value={search} onSearch={setSearch} placeholder="Search cases..." />
            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="documents_uploaded">Documents Uploaded</option>
              <option value="in_process">In Process</option>
              <option value="pending_clarification">Pending Clarification</option>
              <option value="completed">Completed</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={cases}
          loading={loading}
          onRowClick={(row) => navigate(`/cases/${row.id}`)}
          emptyMessage="No cases assigned to your university"
        />
      </div>
    </div>
  );
}
